// components/Sender/workers.js

// ═════ Провайдер аккаунта (транспорт) ═════
export const WORKER_KIND = {
  gmail:   { icon: "📧", label: "Google Workspace", color: "#ea4335", bg: "#fee2e2" },
  outlook: { icon: "📨", label: "Outlook",          color: "#0078d4", bg: "#dbeafe" },
  proton:  { icon: "🔐", label: "Proton",           color: "#6d4aff", bg: "#ede9fe" },
  unknown: { icon: "❓", label: "без аккаунта",      color: "#6b7280", bg: "#f3f4f6" },
};

// ═════ Назначение воркера (канал) ═════
export const WORKER_TYPE = {
  email:           { icon: "📧", label: "Email" },
  contact:         { icon: "📝", label: "Contact form" },
  calendly:        { icon: "📅", label: "Calendly" },
  contactAndEmail: { icon: "✉️", label: "Email + Contact" },
};

const _normType = (v) => {
  const s = String(v || "").trim();
  if (!s) return "";
  const low = s.toLowerCase();
  if (low === "email") return "email";
  if (low === "contact") return "contact";
  if (low === "calendly") return "calendly";
  if (low === "contactandemail" || low === "emailandcontact" ||
      low === "both" || low === "contact_and_email") return "contactAndEmail";
  return s;
};

function _detectKind(w, account) {
  const accProv = String(account?.provider || "").toUpperCase();
  if (accProv) {
    if (accProv.includes("GMAIL") || accProv === "GOOGLE" ||
        accProv.includes("WORKSPACE")) return "gmail";
    if (accProv.includes("OUTLOOK") || accProv.includes("MICROSOFT")) return "outlook";
    if (accProv.includes("PROTON")) return "proton";
  }
  const emailCh = w?.config?.channels?.EMAIL || {};
  const ep = String(emailCh.provider || "").toUpperCase();
  if (ep) {
    if (ep.includes("GMAIL") || ep === "GOOGLE" || ep.includes("WORKSPACE")) return "gmail";
    if (ep.includes("OUTLOOK") || ep.includes("MICROSOFT")) return "outlook";
    if (ep.includes("PROTON")) return "proton";
  }
  const nm = String(w?.name || w?.worker_name || "").toLowerCase();
  if (nm.includes("gmail") || nm.includes("google") || nm.includes("workspace")) return "gmail";
  if (nm.includes("outlook") || nm.includes("microsoft") || nm.includes("hotmail")) return "outlook";
  if (nm.includes("proton")) return "proton";
  return "unknown";
}

function _pripiska(account, workerName) {
  const direct = account?.config?.pripiska;
  if (direct) return String(direct);
  const m = String(workerName || "").match(/^WORKER_[^_]+_\d+_(.+)$/);
  return m ? m[1] : "";
}

function _decorateWorker(w, acc) {
  return {
    ...w,
    _kind: _detectKind(w, acc),
    _type: _normType(w.worker_type) || "email",
    _provider: String(acc?.provider || "").toUpperCase(),
    _pripiska: _pripiska(acc, w.name),
    _account_email: acc?.email || "",
    _account_id: acc?.id ?? w.account ?? null,
    _daily_limit: Number(w.daily_limit ?? 0),
    _is_active: !!w.is_active,
    _name: w.name || w.worker_name || "",
  };
}

export function flattenWorkers(raw) {
  if (!raw) return [];
  const out = [];
  if (raw.accounts || raw.orphan_workers) {
    (raw.accounts || []).forEach((acc) => {
      (acc.workers || []).forEach((w) => out.push(_decorateWorker(w, acc)));
    });
    (raw.orphan_workers || []).forEach((w) => out.push(_decorateWorker(w, null)));
    return out;
  }
  if (Array.isArray(raw)) return raw.map((w) => _decorateWorker(w, null));
  if (Array.isArray(raw.results)) return raw.results.map((w) => _decorateWorker(w, null));
  return [];
}

// ═════ Группировка воркеров по ID ═════
export function workerNumericId(workerName) {
  const s = String(workerName || "").trim();
  const m = s.match(/^WORKER_[^_]+_(\d+)_/);
  return m ? m[1] : null;
}

export function workerGroupKey(workerName) {
  const id = workerNumericId(workerName);
  if (id) return `id:${id}`;
  return `name:${String(workerName || "").trim()}`;
}

// Собрать все group keys, которые можно вывести из одного воркера.
// Учитываем _name, name и worker_name — они могут отличаться.
export function workerAllGroupKeys(w) {
  const keys = new Set();
  [w?.worker_name, w?._name, w?.name].forEach((n) => {
    if (!n) return;
    keys.add(workerGroupKey(n));
  });
  return keys;
}

// То же, но от простого массива имён.
export function namesToGroupKeys(names) {
  const s = new Set();
  (names || []).forEach((n) => {
    if (!n) return;
    s.add(workerGroupKey(n));
  });
  return s;
}

export function buildWorkerUsageIndex(blocks, segmentsById) {
  const byGroup = {};
  const byName = {};
  (blocks || []).forEach((b, blockIdx) => {
    const seg = segmentsById?.[b.segment_id];
    const segName = seg?.name || `⚠️ ${b.segment_id || "?"}`;
    (b.workers || []).forEach((w) => {
      const entry = {
        blockIdx,
        blockNum: blockIdx + 1,
        segmentId: b.segment_id,
        segmentName: segName,
        workerName: w.worker_name,
        channel: w.channel,
        percent: w.percent,
        runtimeId: w.runtime_id || null,
      };
      const gk = workerGroupKey(w.worker_name);
      if (!byGroup[gk]) byGroup[gk] = [];
      byGroup[gk].push(entry);
      if (!byName[w.worker_name]) byName[w.worker_name] = [];
      byName[w.worker_name].push(entry);
    });
  });
  return { byGroup, byName };
}

export function findOtherUsages(usageIndex, workerName, currentBlockIdx) {
  const gk = workerGroupKey(workerName);
  const list = usageIndex.byGroup[gk] || [];
  return list.filter((u) => u.blockIdx !== currentBlockIdx);
}

// ═════ Фильтрация ═════
export function filterWorkers(workers, filters) {
  const {
    kinds, types, providers, pripiskas,
    search = "", onlyActive = true,
    excludeNames,
    excludeGroupKeys,
  } = filters || {};

  const searchLow = String(search || "").trim().toLowerCase();

  return (workers || []).filter((w) => {
    if (onlyActive && !w._is_active) return false;

    if (excludeNames && excludeNames.has(w._name)) return false;

    // Проверяем ВСЕ возможные имена воркера — вдруг в API worker_name и
    // name разные, и в блоке лежит одно из них.
    if (excludeGroupKeys) {
      const keys = workerAllGroupKeys(w);
      for (const k of keys) {
        if (excludeGroupKeys.has(k)) return false;
      }
    }

    if (kinds?.size && !kinds.has(w._kind)) return false;
    if (types?.size && !types.has(w._type)) return false;
    if (providers?.size && !providers.has(w._provider)) return false;
    if (pripiskas?.size && !pripiskas.has(w._pripiska)) return false;
    if (searchLow && !w._name.toLowerCase().includes(searchLow)) return false;
    return true;
  });
}

export function uniqueValues(workers, key) {
  const s = new Set();
  (workers || []).forEach((w) => {
    const v = w[key];
    if (v) s.add(v);
  });
  return Array.from(s).sort();
}

export function pickRandom(arr, n) {
  const a = [...(arr || [])];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, Math.min(n, a.length));
}